import React from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';
import { Button, Dropdown, Menu, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDrag } from 'react-use-gesture';
import { useSprings, animated } from '@react-spring/web';
import { PlusOutlined, FontSizeOutlined } from '@ant-design/icons';
import { clamp } from 'lodash';
import { articleContentType } from 'common/constants/content';
import { fn, swap } from './utils';
import {
  IconMore,
  IconTitlePlaceholder,
  IconSubtitlePlaceholder,
  IconTextareaPlaceholder,
  IconImagePlaceholder,
  IconVideoPlaceholder,
} from 'common/components/svgs';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    height: '100%',
    padding: '24px 8px',
    boxSizing: 'border-box',
    backgroundColor: theme.colors.gray1,
    boxShadow: 'inset -1px 0px 0px rgba(0, 0, 0, 0.09)',
  },
  addSection: {
    marginBottom: 24,
    textAlign: 'center',

    '& .ant-dropdown': {
      width: 240,
      top: '64px !important',
      left: '244px !important',
      minWidth: '240px !important',
      padding: 16,
      backgroundColor: theme.colors.gray1,
      boxShadow:
        '0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
    },
  },
  addSectionBtn: {
    width: 208,
    ...theme.typography.body2,
    color: theme.colors.bodyColor,
  },
  overlayNotice: {
    ...theme.typography.body5,
    color: theme.colors.captionColor,
  },
  overlayMenu: {
    borderRight: 'none',
    marginTop: 12,
  },
  overlayMenuItem: {
    height: '120px !important',
    padding: '0 !important',
    marginTop: '0px !important',
    marginBottom: '12px !important',
    border: `1px solid ${theme.colors.gray5}`,

    '&.ant-menu-item-selected': {
      backgroundColor: `${theme.colors.gray1} !important`,
    },

    '& .icon-bg': {
      backgroundColor: theme.colors.gray2,
    },

    '&:hover': {
      borderColor: theme.colors.mainGreen5,

      '& span': {
        color: `${theme.colors.mainGreen5} !important`,
      },
      '& .icon-cate svg': {
        fill: `${theme.colors.mainGreen5} !important`,
      },
      '& .icon-bg': {
        backgroundColor: theme.colors.mainGreen1,
      },
    },
  },
  menuItemHeader: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    paddingLeft: 16,

    '& svg': {
      fill: theme.colors.titleColor,
    },
  },
  menuItemCategory: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
  },
  sectionList: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionItem: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    height: 40,
    paddingLeft: 24,
    cursor: 'grab',
    transition: 'all 0.01 linear',

    '&:hover': {
      backgroundColor: theme.colors.gray2,
    },
  },
  sectionItemFirst: {
    cursor: 'pointer',
  },
  sectionItemFocused: {
    borderLeft: `2px solid ${theme.colors.mainGreen6}`,
    backgroundColor: `${theme.colors.mainGreen1} !important`,
  },
  sectionItemText: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    paddingLeft: 12,
  },
  sectionItemMore: {
    position: 'absolute',
    top: 13,
    right: 16,
  },
}));

const SideBar = ({ activeIndex, data, handleFocus, handleSortClick, handleAddContent }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const order = data.map((_, idx) => idx);
  const [springs, api] = useSprings(data.length, fn(order), [data]);

  const bind = useDrag(({ args: [originalIndex], active, movement: [, y] }) => {
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(Math.round((curIndex * 40 + y) / 40), 0, data.length - 1);
    const newOrder = swap(order, curIndex, curRow);

    api.start(fn(newOrder, active, originalIndex, curIndex, y));

    if (!active) {
      handleSortClick(newOrder, originalIndex);
    }
  });

  const handleMenuClick = ({ key }) => {
    handleAddContent(key);
  };

  return (
    <div className={classes.container}>
      <div className={classes.addSection} id="add-section">
        <Dropdown
          trigger={['click']}
          placement={'bottomRight'}
          getPopupContainer={() => document.querySelector('#add-section')}
          overlay={
            <div className={classes.overlay}>
              <span className={classes.overlayNotice}>{t('content.article.notice.add')}</span>
              <Menu className={classes.overlayMenu} onClick={handleMenuClick}>
                <Menu.Item className={classes.overlayMenuItem} key={articleContentType.TITLE}>
                  <div className={classes.menuItemHeader}>
                    <FontSizeOutlined className={'icon-cate'} />
                    <span className={classes.menuItemCategory}>
                      {t('content.article_content_type.title')}
                    </span>
                  </div>
                  <IconTitlePlaceholder className={'icon-bg'} />
                </Menu.Item>
                <Menu.Item className={classes.overlayMenuItem} key={articleContentType.SUBTITLE}>
                  <div className={classes.menuItemHeader}>
                    <FontSizeOutlined className={'icon-cate'} />
                    <span className={classes.menuItemCategory}>
                      {t('content.article_content_type.subtitle')}
                    </span>
                  </div>
                  <IconSubtitlePlaceholder className={'icon-bg'} />
                </Menu.Item>
                <Menu.Item className={classes.overlayMenuItem} key={articleContentType.TEXTAREA}>
                  <div className={classes.menuItemHeader}>
                    <FontSizeOutlined className={'icon-cate'} />
                    <span className={classes.menuItemCategory}>
                      {t('content.article_content_type.textarea')}
                    </span>
                  </div>
                  <IconTextareaPlaceholder className={'icon-bg'} />
                </Menu.Item>
                <Menu.Item className={classes.overlayMenuItem} key={articleContentType.IMAGE}>
                  <div className={classes.menuItemHeader}>
                    <Icon type="picture" className={'icon-cate'} />
                    <span className={classes.menuItemCategory}>
                      {t('content.article_content_type.image')}
                    </span>
                  </div>
                  <IconImagePlaceholder className={'icon-bg'} />
                </Menu.Item>
                <Menu.Item className={classes.overlayMenuItem} key={articleContentType.VIDEO}>
                  <div className={classes.menuItemHeader}>
                    <Icon type="video-camera" className={'icon-cate'} />
                    <span className={classes.menuItemCategory}>
                      {t('content.article_content_type.video')}
                    </span>
                  </div>
                  <IconVideoPlaceholder className={'icon-bg'} />
                </Menu.Item>
              </Menu>
            </div>
          }
        >
          <Button block size="large" className={classes.addSectionBtn}>
            <PlusOutlined />
            {t('content.article.btn.add')}
          </Button>
        </Dropdown>
      </div>

      <div className={classes.sectionList}>
        <div className={classes.sectionWrapper}>
          {springs.map(({ zIndex, shadow, y, scale }, i) => (
            <animated.div
              {...bind(i)}
              key={i}
              style={{
                zIndex,
                y,
                shadow,
                scale,
              }}
              className={classNames(classes.sectionItem, {
                [classes.sectionItemFocused]: activeIndex === i,
              })}
              onClick={() => handleFocus(i)}
            >
              <Choose>
                <When condition={data?.[i]?.type === articleContentType.IMAGE}>
                  <Icon type="picture" />
                </When>
                <When condition={data?.[i]?.type === articleContentType.VIDEO}>
                  <Icon type="video-camera" />
                </When>
                <Otherwise>
                  <FontSizeOutlined />
                </Otherwise>
              </Choose>
              <span className={classes.sectionItemText}>{`${t(
                `content.article_content_type.${data?.[i]?.type?.toLowerCase()}`,
              )} ${data?.[i]?.index}`}</span>

              <IconMore className={classes.sectionItemMore} />
            </animated.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SideBar;
